
import {
  Component,
  OnDestroy,
  OnInit,
  HostListener,
  Inject,
  PLATFORM_ID
} from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription, finalize } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

import { CartService } from '../../../../services/cart';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { UrlSlugifyService } from '../../../../services/url-slugify';
import { PetServices } from '../../../../services/petservices';

@Component({
  selector: 'app-clinicservices',
  templateUrl: './clinicservices.component.html',
  styleUrl: './clinicservices.component.scss'
})
export class ClinicservicesComponent implements OnInit, OnDestroy {
  services: any[] = [];

  page = 1;
  perPage = 10;
  lastPage = 1;
  total = 0;

  isLoading = false;
  isLoadingMore = false;
  noData = false;

  isMobile = false;
  isAr = false;

  private isBrowser: boolean;
  private langSub?: Subscription;

  constructor(
    private petServices: PetServices,
    private cartService: CartService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private slugify: UrlSlugifyService,
    private transloco: TranslocoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    // SSR safe
    if (!this.isBrowser) {
      return;
    }

    this.isMobile = this.deviceService.isMobileDevice();

    this.isAr = this.transloco.getActiveLang() === 'ar';
    this.langSub = this.transloco.langChanges$.subscribe(lang => {
      this.isAr = lang === 'ar';
    });

    this.getClinicServices(false);
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  getClinicServices(loadMore: boolean = false): void {
    if (this.isLoading || this.isLoadingMore) return;

    if (loadMore) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.page = 1;
      this.services = [];
      this.noData = false;
    }

    this.petServices.getAll(this.page, this.perPage)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isLoadingMore = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          const items = res?.data ?? [];

          this.services = loadMore
            ? [...this.services, ...items]
            : items;

          // Laravel normal pagination
          this.lastPage = res?.last_page ?? res?.meta?.last_page ?? 1;
          this.total = res?.total ?? res?.meta?.total ?? this.services.length;

          this.noData = this.services.length === 0;
        },
        error: (err) => {
          console.error('Failed to load clinic services', err);
          this.noData = true;
        }
      });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!this.isBrowser) return;
    if (this.isLoading || this.isLoadingMore) return;
    if (this.noData) return;
    if (this.page >= this.lastPage) return;

    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    const bottomReached =
      scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.page++;
      this.getClinicServices(true);
    }
  }

serviceDetails(service: any): void {
  this.router.navigate([
    '/clinic-service',
    this.slugify.slugify(service.name_en || service.name_ar || 'service'),
    service.id
  ]);
}

  addToCart(service: any): void {
    const serviceItem = {
      ...service,
      quantity: 1,
      is_service: true,
      type: 'clinic_service'
    };

    this.cartService.addToCart(serviceItem);
  }

  calcDis(newP: any, oldP: any): number {
    const newPrice = Number(newP);
    const oldPrice = Number(oldP);

    if (!newPrice || !oldPrice || oldPrice <= newPrice) {
      return 0;
    }

    return Math.round(((oldPrice - newPrice) / oldPrice) * 100);
  }

  getServicePrice(service: any): number {
    return Number(service.price);
  }

  fixImage(url: any): string {
    if (!url) return '';

    if (url.startsWith('http')) {
      return url;
    }

    return 'https://backendproduction.blueboxpet.com/storage/app/public/' + url;
  }

  getFallbackImage(service: any): string {
    if (service?.pet === '0' || service?.pet_type === '0') {
      return '/assets/noproduct cat.webp';
    }

    return '/assets/noproduct dog.png';
  }
}